import React, { useState, useEffect, useRef } from "react";
import { MapPin, Search, X, AlertCircle, RefreshCw } from "lucide-react";
import { analytics } from "../../utils/analytics";
import {
  useGooglePlaces,
  PlacesPrediction,
} from "../../context/GooglePlacesContext";

interface AddressSearchProps {
  value: string;
  onChange: (
    address: string,
    placeId?: string,
    coordinates?: { lat: number; lng: number }
  ) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  required?: boolean;
  id?: string;
  trackingId?: string;
  page?: string;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  value,
  onChange,
  placeholder = "Enter your address...",
  className = "",
  error,
  required = false,
  id = "address-search",
  trackingId,
  page = "address_search",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [predictions, setPredictions] = useState<PlacesPrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use Google Places context
  const {
    isLoaded,
    isLoading,
    error: placesError,
    getAddressPredictions,
    getPlaceDetails,
    retry,
  } = useGooglePlaces();

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (inputValue.length > 2) {
      // Debounce the search
      searchTimeoutRef.current = setTimeout(() => {
        searchAddresses(inputValue);
      }, 300);
    } else {
      setPredictions([]);
      setIsOpen(false);
    }
  };

  const searchAddresses = async (query: string) => {
    setLoading(true);
    try {
      // Always try to get predictions - the lazy loading will handle initialization
      const results = await getAddressPredictions(query);

      setPredictions(results);
      setIsOpen(results.length > 0);
      setSelectedIndex(-1);

      // Track the search query and results
      analytics.track("address_search", page, {
        query,
        resultsCount: results.length,
        componentId: trackingId || id,
        timestamp: new Date().toISOString(),
        googlePlacesLoaded: isLoaded,
      });
    } catch (error) {
      console.error("Address search failed:", error);
      setPredictions([]);
      setIsOpen(false);

      // Track search failure
      analytics.trackError(
        "address_search_error",
        error instanceof Error ? error.message : "Unknown error",
        {
          query,
          componentId: trackingId || id,
        }
      );
    } finally {
      setLoading(false);
    }
  };
  const handlePredictionSelect = async (prediction: PlacesPrediction) => {
    setIsOpen(false);
    setPredictions([]);

    // Get detailed place information if available
    if (isLoaded && getPlaceDetails) {
      try {
        const details = await getPlaceDetails(prediction.place_id);
        if (details) {
          // Pass coordinates to the onChange callback
          const coordinates = {
            lat: details.geometry.location.lat,
            lng: details.geometry.location.lng,
          };

          onChange(prediction.description, prediction.place_id, coordinates);

          analytics.track("address_details_fetched", page, {
            componentId: trackingId || id,
            placeId: prediction.place_id,
            hasGeometry: !!details.geometry,
            addressComponentsCount: details.address_components.length,
            coordinates: coordinates,
          });
        } else {
          // Fallback without coordinates
          onChange(prediction.description, prediction.place_id);
        }
      } catch (error) {
        console.error("Failed to get place details:", error);
        // Fallback without coordinates
        onChange(prediction.description, prediction.place_id);
      }
    } else {
      // Fallback without coordinates
      onChange(prediction.description, prediction.place_id);
    }

    analytics.track("address_selected", page, {
      componentId: trackingId || id,
      placeId: prediction.place_id,
      address: prediction.description,
      timestamp: new Date().toISOString(),
      selectionMethod: "click",
    });

    analytics.trackSelectionChange(
      trackingId || id,
      page,
      prediction.description
    );

    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handlePredictionSelect(predictions[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleClear = () => {
    onChange("");
    setPredictions([]);
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleBlur = () => {
    // Delay closing to allow for click on predictions
    setTimeout(() => {
      setIsOpen(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleFocus = () => {
    // If there are existing predictions, show them
    if (predictions.length > 0) {
      setIsOpen(true);
    }

    // Trigger lazy initialization of Google Places API on first focus
    // This ensures the API is loaded when the user starts interacting with the field
    if (!isLoaded && !isLoading) {
      // Call getAddressPredictions with an empty string to trigger lazy initialization
      // This won't return results but will initialize the Places API
      getAddressPredictions("").catch(console.error);
    }
  };
  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          ) : (
            <MapPin className="h-4 w-4 text-gray-400" />
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          className={`
            block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-400
            ${error ? "border-red-500" : "border-gray-300 dark:border-gray-600"}
          `}
        />

        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Predictions dropdown */}
      {isOpen && predictions.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {predictions.map((prediction, index) => (
            <li
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
              className={`
                px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0
                ${
                  index === selectedIndex
                    ? "bg-blue-50 dark:bg-blue-900/20"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700"
                }
              `}
            >
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {prediction.main_text}
                  </p>
                  {prediction.secondary_text && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {prediction.secondary_text}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* No results message */}
      {!loading && value.length > 2 && predictions.length === 0 && isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg p-4">
          <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
            <Search className="h-4 w-4" />
            <span className="text-sm">
              {!isLoaded ? "Loading address search..." : "No addresses found"}
            </span>
          </div>
        </div>
      )}

      {/* Google Places loading/error status */}
      {placesError && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 rounded-md shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Address search unavailable</span>
            </div>
            <button
              onClick={retry}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
            >
              <RefreshCw className="h-3 w-3" />
              <span>Retry</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSearch;
